import {
  Box,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { ChangeEvent, useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import InfoTooltip from "../../../common/components/functional/InfoTooltip/InfoTooltip";
import CustomAutocompleteMulti from "../../../common/components/functional/customAutocomplete/CustomAutocompleteMulti";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import isEmpty from "../../../common/helpers/isEmpty";
import { AutocompleteType } from "../../../common/types/autoComplete";
import {
  fetchCustomerCostCenters,
  selectAllCustomerCostCenters,
} from "../customerCostCentersSlice";
import { selectCustomerOrganizationById } from "../customerOrganizationsSlice";
import { CustomerCC } from "../types/queries";
import { NAME_SPACE } from "./WorkLocationsForm";

const CostCenters = () => {
  const [searchParams] = useSearchParams();
  const customerOrgId = searchParams.get("org");

  const dispatch = useAppDispatch();

  const selectedOrganization = useAppSelector((state) =>
    selectCustomerOrganizationById(state, customerOrgId as string),
  );

  // TODO: replace with autocomplete query
  const allCustomerCostCenters = useAppSelector((state) =>
    selectAllCustomerCostCenters(state),
  );

  // filter customer cost centers that are not from the selected organization and inactive
  const customerCostCenters = useMemo(
    () =>
      allCustomerCostCenters
        .filter((cc) => cc.customerOrgId === customerOrgId && cc.active)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allCustomerCostCenters, customerOrgId],
  );

  const formattedCustomerCostCenters = customerCostCenters.map(
    ({ customerCCId, name }) => ({
      id: customerCCId,
      name,
    }),
  );

  const { t } = useTranslation();

  const {
    control,
    watch,
    setValue,
    trigger,
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext();

  const Tooltip = (
    <Box display="flex" alignItems="center" marginLeft="13px">
      <InfoTooltip title={t(`${NAME_SPACE}:costCentersTooltip`)} />
    </Box>
  );

  const handleRadioButtonChange = (
    event: ChangeEvent<HTMLInputElement>,
    onChange: Function,
  ) => {
    const value = event.target.value;
    if (value === "true") {
      onChange(true);
      if (errors["customerCCs"]) {
        // if allowUsingAllCostCenters is true, customerCCs are not required
        clearErrors("customerCCs");
      }
    } else {
      onChange(false);
      // if allowUsingAllCostCenters is false, customerCCs are required
      watchedCustomerCCs.length === 0 &&
        setError("customerCCs", {
          type: "required",
          message: "Vaadittu kenttä",
        });
    }
  };

  // Fetch customer cost centers
  useEffect(() => {
    if (selectedOrganization) {
      dispatch(
        fetchCustomerCostCenters({
          customerCompanyId: selectedOrganization.customerCompanyId,
        }),
      );
    }
  }, [dispatch, selectedOrganization]);

  const watchedCustomerCCs = watch("customerCCs");

  //  Determine if radio button selection should be "All" or "Selected only" from existing data
  useEffect(() => {
    const shouldUseAllCostCenters = isEmpty(watchedCustomerCCs);

    setValue(
      "allowUsingAllCostCenters",
      shouldUseAllCostCenters ? true : false,
    );
  }, [watchedCustomerCCs, setValue]);

  const watchedAllowUsingAllCostCenters = watch("allowUsingAllCostCenters");

  // Check customerCCs validity -> if valid, trigger form validation
  useEffect(() => {
    if (watchedCustomerCCs?.length > 0) {
      void trigger();
    }
  }, [watchedCustomerCCs, trigger]);

  // trigger form validation if allowUsingAllCostCenters is true
  useEffect(() => {
    if (watchedAllowUsingAllCostCenters) {
      void trigger();
    }
  }, [watchedAllowUsingAllCostCenters, trigger, clearErrors]);

  return (
    <FormSection
      title={t("customers:customerCostCenters")}
      headerSx={{ minWidth: "214px" }}
      infoTooltip={Tooltip}
    >
      <Grid container item xs={3} justifyContent="flex-end">
        <Typography
          sx={{ marginTop: "0.75rem" }}
          variant="secondary"
          textAlign="right"
        >
          {t(`${NAME_SPACE}:selectedCostCenters`)}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          name="allowUsingAllCostCenters"
          control={control}
          render={({ field }) => (
            <RadioGroup
              value={field.value}
              onChange={(e) => handleRadioButtonChange(e, field.onChange)}
            >
              <FormControlLabel
                sx={{ width: "fit-content" }}
                checked={field.value === true}
                label={t(`${NAME_SPACE}:allowUsingAllCostCenters`)}
                value={true}
                control={<Radio />}
              />
              <Box display="flex">
                <FormControlLabel
                  checked={field.value === false}
                  sx={{ minWidth: "119px" }}
                  label={t(`${NAME_SPACE}:selectedOnly`)}
                  value={false}
                  control={<Radio />}
                />

                <Box display="flex" alignItems="center" width="100%">
                  <Controller
                    name="customerCCs"
                    control={control}
                    rules={{
                      required: {
                        value: watchedAllowUsingAllCostCenters ? false : true,
                        message: t("common:requiredField"),
                      },
                    }}
                    render={({ field: { value, onChange } }) => (
                      <CustomAutocompleteMulti
                        disabled={field.value === true}
                        resetValues={field.value === true}
                        options={formattedCustomerCostCenters}
                        defaultValue={value.map((v: CustomerCC) => ({
                          id: v.customerCCId,
                          name: v.name,
                        }))}
                        isOptionEqualToValue={(option, name) =>
                          option.id === name.id
                        }
                        fullWidth
                        name="customerCCs"
                        onChange={(_: any, newValue: AutocompleteType[]) => {
                          const formattedValue = newValue.map((v) => ({
                            customerCCId: v.id,
                            name: v.name,
                          }));
                          onChange(formattedValue);
                        }}
                        textFieldError={!!errors.customerCCs}
                      />
                    )}
                  />
                </Box>
              </Box>
            </RadioGroup>
          )}
        />
      </Grid>
    </FormSection>
  );
};

export default CostCenters;
